/**
 * Timestamp utility functions for current action feature
 * Feature: 003-c-program-files - Send Action to Device
 */

import type { TFunction } from 'i18next';
import { CurrentActionFormData, CurrentAction } from '../types/currentAction';

/**
 * Convert form data (timeToStart in minutes) to startTimestamp (Unix milliseconds)
 *
 * @param timeToStartMinutes - Minutes from now when action should start
 * @returns Unix timestamp in milliseconds
 */
export function formDataToTimestamp(timeToStartMinutes: number): number {
  return Date.now() + (timeToStartMinutes * 60 * 1000);
}

/**
 * Convert startTimestamp (Unix milliseconds) to timeToStart (minutes from now)
 * Used for pre-filling edit dialog
 *
 * @param startTimestamp - Unix timestamp in milliseconds
 * @returns Minutes until start (0 if already started or in past)
 */
export function timestampToTimeToStart(startTimestamp: number): number {
  return Math.max(0, Math.floor((startTimestamp - Date.now()) / 60000));
}

/**
 * Format timestamp as relative time string for display, in the language of `t`
 *
 * @param startTimestamp - Unix timestamp in milliseconds
 * @param t - i18next translate function (keys under `relativeTime`)
 * @returns Localised text for how far the start is from now
 */
export function formatRelativeTime(startTimestamp: number, t: TFunction): string {
  const minutesUntilStart = Math.floor((startTimestamp - Date.now()) / 60000);
  if (minutesUntilStart === 0) return t('relativeTime.startingNow');

  const minutes = Math.abs(minutesUntilStart);
  const [unit, count] =
    minutes < 60 ? ['Minutes', minutes]
    : minutes < 60 * 24 ? ['Hours', Math.floor(minutes / 60)]
    : ['Days', Math.floor(minutes / (60 * 24))];

  return t(minutesUntilStart > 0 ? `relativeTime.startsIn${unit}` : `relativeTime.started${unit}`, { count });
}

/**
 * Convert CurrentAction to form data for edit dialog pre-fill
 *
 * @param currentAction - The current action from Firebase
 * @returns Form data with calculated timeToStart and stay conditions
 */
export function currentActionToFormData(currentAction: CurrentAction): CurrentActionFormData {
  const timeToStart = timestampToTimeToStart(currentAction.startTimestamp);

  return {
    timeToStart,
    stayUntilParentApproves: currentAction.stayUntilParentApproves,
    stayUntilChecked: currentAction.stayUntilChecked,
    stayUntilFixedTime: currentAction.duration > 0,
    duration: currentAction.duration > 0 ? currentAction.duration : 30, // Default 30 if 0
  };
}

/**
 * Format a timestamp as local HH:mm (24-hour), as the current-action panel's lines show it
 *
 * @param timestamp - Unix timestamp in milliseconds
 */
export function formatClockTime(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
